import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Input,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { FilterBar } from '../../components/FilterBar';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { CopyChip } from '../../components/CopyChip';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useUsers } from './useUsers';
import { UserDetailDrawer } from './UserDetailDrawer';
import { UserCreateModal } from './UserCreateModal';

/**
 * Users · Liste + Detail-Drawer.
 *
 * Spec: lmm-wiki/wiki/orbit-specs/done/user-crud-admin.md
 * Routes: /:brandSlug/users  und  /:brandSlug/users/:userId
 *
 * Phase-1 Lieferumfang (vollständig):
 *  - Server-side Search via `?q=` + Status-Filter
 *  - Row-Klick → Detail-Drawer
 *  - Create-Modal mit OneShotSecretReveal für generatedPassword
 *  - Edit-Modal mit Diff-Patch
 *  - Suspend / Lift mit ConfirmDialog / Reason-Form
 *  - VAT-Reverify mit BZSt-Result-Modal
 *  - Anonymize mit ConfirmStringDialog
 *  - Tenant Add/Revoke (Brand-Membership-Management)
 *  - Tenant-Empty-State für JIT-Operatoren
 */
export function UsersPage() {
  const { userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const usersQuery = useUsers({ search, status });

  const openDetail = (id) => navigate(`../users/${id}`, { relative: 'path' });
  const closeDetail = () => navigate('../users', { relative: 'path' });

  return (
    <div>
      <PageHeader
        title="Benutzer"
        subtitle="Liste aller User dieser Brand · GET /v1/admin/users"
        actions={
          <HasPermission perms={['users.write']}>
            <Button color="primary" onClick={() => setCreateOpen(true)}>
              + Neuer Benutzer
            </Button>
          </HasPermission>
        }
      />

      <FilterBar
        search={search}
        searchPlaceholder="Email · Name · Kundennummer…"
        onSearchChange={setSearch}
        onReset={
          search || status
            ? () => {
                setSearch('');
                setStatus('');
              }
            : undefined
        }
        extras={
          <Input
            type="select"
            bsSize="sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ maxWidth: 160 }}
          >
            <option value="">Alle Status</option>
            <option value="active">Aktiv</option>
            <option value="suspended">Gesperrt</option>
            <option value="deleted">Gelöscht</option>
          </Input>
        }
      />

      <DataTable
        loading={usersQuery.isLoading}
        error={usersQuery.error}
        empty={!usersQuery.data?.users?.length}
        emptyMessage={
          search || status
            ? 'Keine Treffer für die aktuellen Filter.'
            : 'Noch keine Benutzer in dieser Brand.'
        }
        onRetry={() => usersQuery.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 90 }}>ID</th>
            <th>Email</th>
            <th>Name</th>
            <th style={{ width: 110 }}>Rolle</th>
            <th style={{ width: 130 }}>Status</th>
            <th style={{ width: 140 }}>Erstellt</th>
            <th style={{ width: 140 }}>Letzter Login</th>
          </tr>
        </thead>
        <tbody>
          {usersQuery.data?.users?.map((u) => (
            <tr
              key={u.id}
              role="button"
              onClick={() => openDetail(u.id)}
              className={u.status === 'deleted' ? 'opacity-50' : undefined}
            >
              <td>
                <CopyChip value={u.customerNumber} label="Kundennummer" />
              </td>
              <td>{u.email}</td>
              <td>{u.displayName || <span className="text-muted">—</span>}</td>
              <td>
                <StatusBadge value={u.role} />
              </td>
              <td>
                <StatusBadge value={u.status} />
              </td>
              <td>
                <RelativeTime iso={u.createdAt} />
              </td>
              <td>
                <RelativeTime iso={u.lastLoginAt} />
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      {usersQuery.data?.users?.length > 0 && (
        <p className="text-muted small mt-2 mb-0">
          {usersQuery.data.users.length} von {usersQuery.data.total ?? usersQuery.data.users.length} Einträgen ·
          Server-Limit 200 — bei Treffer-Overflow Suche präzisieren
        </p>
      )}

      <UserDetailDrawer
        userId={paramUserId}
        isOpen={!!paramUserId}
        onClose={closeDetail}
      />

      <UserCreateModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
